import { getLocalToday } from "utils/Common/Date";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import useGetProject from "../../../../hooks/useGetProject";
import {
  saveGcDraw,
  getDraws,
  getDraw,
} from "../../../../services/drawService";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import ConstructionProgressStep from "../components/ConstructionProgressStep";
import DrawDetailsStep from "../components/DrawDetailsStep";
import LienWaiversStep from "../components/LienWaiversStep";
import LineItemsStep from "../components/LineItemsStep";
import ReviewSubmitStep from "../components/ReviewSubmitStep";
import StepTabs from "../components/StepTabs";
import SuccessStep from "../components/SuccessStep";
import { useGcLayout } from "../../GcLayout";
import useScrollToTop from "hooks/useScrollToTop";

interface DrawDetails {
  bank: string;
  drawNo: string;
  date: string;
  location: string;
  borrower: string;
  contractor: string;
  phone: string;
  email: string;
  periodTo: string;
  payee: string;
  mailTo: string;
}

export interface DrawLineItem {
  id: number;
  sovItemId: string;
  aiaItemId: string;
  aiaItemCode: number | null;
  aiaDescription: string;
  subContractorId: string;
  vendor: string;
  amount: string;
  sovAmount: number | null;
  invoiceKey: string;
  invoiceName: string;
  notes: string;
  invoiceAttached: boolean;
  offsiteStored: boolean;
  offsiteAmount: string;
  offsiteNote: string;
}

export interface SovItemPayload {
  id: string;
  aiaItemId: string;
  aiaItemCode: number;
  description: string;
  subContractor: { id: string; companyName: string } | null;
  amount: { amount: number } | null;
}

interface SummaryItem {
  label: string;
  value: string;
  accent?: boolean;
}

let _lineIdCounter = Date.now();
const nextLineIdVal = () => ++_lineIdCounter;

const getLineItemMatchKey = (item: {
  aiaItemId?: string | null;
  subContractorId?: string | null;
  amount?: string | null;
  notes?: string | null;
}) =>
  [
    item.aiaItemId ?? "",
    item.subContractorId ?? "",
    item.amount ?? "",
    item.notes ?? "",
  ].join("::");

const mapDrawLineItems = (
  draw: any,
  previousLineItems: DrawLineItem[] = [],
): DrawLineItem[] => {
  const previousByKey = new Map(
    previousLineItems.map((item) => [getLineItemMatchKey(item), item]),
  );

  return (draw?.lienItems ?? []).map((item: any) => {
    const amount = String(item.totalAmount?.amount ?? "");
    const notes = item.description ?? "";
    const invoiceKey =
      item.invoice?.key ??
      item.invoiceUrl?.key ??
      (typeof item.invoiceUrl === "string" ? item.invoiceUrl : "");
    const previousItem = previousByKey.get(
      getLineItemMatchKey({
        aiaItemId: item.aiaItemId,
        subContractorId: item.subContractorId,
        amount,
        notes,
      }),
    );

    return {
      id: previousItem?.id ?? nextLineIdVal(),
      sovItemId: item.id,
      aiaItemId: item.aiaItemId,
      aiaItemCode: item.aiaItem?.itemCode ?? null,
      aiaDescription: item.aiaItem?.itemName ?? "",
      subContractorId: item.subContractorId,
      vendor: item.subContractor?.companyName ?? "",
      amount,
      sovAmount: null,
      invoiceKey: invoiceKey || previousItem?.invoiceKey || "",
      invoiceName: item.invoice?.fileName || previousItem?.invoiceName || "",
      invoiceAttached: Boolean(invoiceKey || previousItem?.invoiceAttached),
      notes,
      offsiteStored: !!item.storedItemAmount,
      offsiteAmount: item.storedItemAmount
        ? String(item.storedItemAmount.amount)
        : "",
      offsiteNote:
        "Materials purchased and stored off-site, not yet incorporated into the work",
    };
  });
};

const today = getLocalToday();

const initialDrawDetails: DrawDetails = {
  bank: "",
  drawNo: "01",
  date: today,
  location: "",
  borrower: "",
  contractor: "",
  phone: "",
  email: "",
  periodTo: "",
  payee: "",
  mailTo: "",
};

const steps = [
  { id: 1, label: "Construction Progress" },
  { id: 2, label: "Draw Details" },
  { id: 3, label: "Line Items & Invoices" },
  { id: 4, label: "Lien Waivers" },
  { id: 5, label: "Sign & Submit" },
];

const parseCurrency = (value: string): number => {
  const amount = Number(value.replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

// sessionStorage keys for the "Add new vendor" round trip to ManageSubContractor.
const DRAW_RETURN_KEY = "draw_return_state";
const NEW_SUBCONTRACTOR_KEY = "sov_new_subcontractor";
const DRAW_STASH_TTL_MS = 30 * 60 * 1000;

interface DrawReturnStash {
  projectId: string;
  drawId: string;
  rowId: number;
  drawDetails: DrawDetails;
  lineItems: DrawLineItem[];
  savedAt: number;
}

// Reads the unsaved draw and selects the new vendor on the row that asked for it.
const readDrawReturnStash = (
  projectId?: string,
  drawId?: string,
): DrawReturnStash | null => {
  try {
    const stash = JSON.parse(
      sessionStorage.getItem(DRAW_RETURN_KEY) || "null",
    ) as DrawReturnStash | null;
    if (
      !stash ||
      stash.projectId !== projectId ||
      stash.drawId !== (drawId ?? "") ||
      Date.now() - stash.savedAt > DRAW_STASH_TTL_MS
    ) {
      return null;
    }
    const created = JSON.parse(
      sessionStorage.getItem(NEW_SUBCONTRACTOR_KEY) || "null",
    ) as { id: string; name: string } | null;
    if (!created) return stash;
    return {
      ...stash,
      lineItems: stash.lineItems.map((item) =>
        item.id === stash.rowId
          ? { ...item, subContractorId: created.id, vendor: created.name }
          : item,
      ),
    };
  } catch {
    return null;
  }
};

const DrawRequestPage = () => {
  const { setCrumbs } = useGcLayout();
  const showToast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId, drawId: drawIdParam } = useParams<{
    projectId: string;
    drawId?: string;
  }>();
  const [returnStash] = useState(() =>
    readDrawReturnStash(projectId, drawIdParam),
  );
  useEffect(() => {
    try {
      sessionStorage.removeItem(DRAW_RETURN_KEY);
      sessionStorage.removeItem(NEW_SUBCONTRACTOR_KEY);
    } catch {}
  }, []);
  const { projectData, loading: projectLoading } = useGetProject(projectId);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const s = Number(searchParams.get("step"));
    return s >= 1 && s <= 6 ? s : 1;
  });
  const goToStep = (step: number) => {
    setCurrentStep(step);
    setSearchParams({ step: String(step) }, { replace: true });
  };
  const [savingDraft, setSavingDraft] = useState(false);
  const [lineItemsError, setLineItemsError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const projectName = (projectData?.projectName as string) ?? "";

  useEffect(() => {
    if (!projectData) return;
    const lender = projectData.lender as Record<string, string>;
    const owner = projectData.owner as Record<string, string>;
    const gc = projectData.gc as Record<string, string>;
    setDrawDetails((prev) => ({
      ...prev,
      bank: lender?.companyName ?? prev.bank,
      location: (projectData.projectAddress as string) ?? prev.location,
      borrower: owner?.companyName ?? prev.borrower,
      contractor: gc?.companyName ?? prev.contractor,
      phone: gc?.phone ?? "",
      email: gc?.email ?? "",
    }));
  }, [projectData]);
  useScrollToTop(currentStep, { skipFirstRender: false });

  useEffect(() => {
    setCrumbs([
      { label: "My Projects", link: "/gc-dashboard" },
      { label: projectName || "Project" },
      { label: "New Draw Request" },
    ]);
  }, [projectName]);

  useEffect(() => {
    if (!projectId || drawIdParam) return;
    getDraws(projectId).then(({ data }: { data: any }) => {
      const list: Array<{ drawNumber: number }> = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      const maxNo = list.reduce(
        (max, d) => Math.max(max, d.drawNumber ?? 0),
        0,
      );
      setDrawDetails((prev) => ({ ...prev, drawNo: String(maxNo + 1) }));
    });
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !drawIdParam || returnStash) return;
    (
      getDraw(projectId, drawIdParam) as Promise<{ data: any; error: any }>
    ).then(({ data, error }) => {
      if (error || !data) return;
      const draw = data?.data ?? data;

      setDrawDetails((prev) => ({
        ...prev,
        drawNo: String(draw.drawNumber ?? prev.drawNo),
        periodTo: draw.toDate ?? prev.periodTo,
        payee: draw.paymentInstructions?.payeeName ?? prev.payee,
        mailTo: draw.paymentInstructions?.mailToAddress ?? prev.mailTo,
      }));
      setLineItems((prev) => mapDrawLineItems(draw, prev));
    });
  }, [drawIdParam, projectId, returnStash]);

  const [savedDraft] = useState<{
    drawDetails?: Partial<DrawDetails>;
    lineItems?: DrawLineItem[];
  } | null>(() => {
    if (drawIdParam) return null;
    try {
      const raw = localStorage.getItem(`draw_draft_${projectId}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [drawDetails, setDrawDetails] = useState<DrawDetails>({
    ...initialDrawDetails,
    ...(returnStash?.drawDetails ?? savedDraft?.drawDetails ?? {}),
    // Always use today's date — a resumed draft shouldn't keep the date
    // from whenever it was first started.
    date: getLocalToday(),
  });
  const [lineItems, setLineItems] = useState<DrawLineItem[]>(
    returnStash?.lineItems ?? savedDraft?.lineItems ?? [],
  );
  const nextLineId = useRef(Date.now());
  const [drawId, setDrawId] = useState<string>(drawIdParam ?? "");

  const handleSetDrawId = (id: string) => {
    localStorage.removeItem(`draw_draft_${projectId}`);
    setDrawId(id);

    if (id) {
      setCurrentStep(4);
      navigate(`/draw-request/${projectId}/${id}?step=4`, { replace: true });
    }
  };
  const [gcSigned, setGcSigned] = useState<boolean>(false);

  useEffect(() => {
    if (drawIdParam) return;
    localStorage.setItem(
      `draw_draft_${projectId}`,
      JSON.stringify({ drawDetails, lineItems }),
    );
  }, [drawDetails, lineItems, drawIdParam]);

  const totalAmount = useMemo<string>(() => {
    const total = lineItems.reduce(
      (sum, item) => sum + parseCurrency(item.amount),
      0,
    );
    return total.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }, [lineItems]);

  const summary = useMemo<SummaryItem[]>(
    () => [
      { label: "Project", value: projectName },
      { label: "Bank", value: drawDetails.bank },
      { label: "Draw Number", value: `#${drawDetails.drawNo || "0"}` },
      { label: "Total Requested", value: totalAmount, accent: true },
      { label: "Line Items", value: `${lineItems.length} vendors` },
      { label: "Period To", value: drawDetails.periodTo },
    ],
    [
      projectName,
      drawDetails.bank,
      drawDetails.drawNo,
      lineItems.length,
      totalAmount,
    ],
  );

  const handleDrawDetailsChange = (key: keyof DrawDetails, value: string) => {
    setDrawDetails((prev) => ({ ...prev, [key]: value }));
  };
  const handleAddLine = () => {
    setLineItems((prev) => [
      ...prev,
      {
        id: ++nextLineId.current,
        sovItemId: "",
        aiaItemId: "",
        aiaItemCode: null,
        aiaDescription: "",
        subContractorId: "",
        vendor: "",
        amount: "",
        sovAmount: null,
        invoiceKey: "",
        invoiceName: "",
        notes: "",
        invoiceAttached: false,
        offsiteStored: false,
        offsiteAmount: "",
        offsiteNote:
          "Materials purchased and stored off-site, not yet incorporated into the work",
      },
    ]);
  };

  const handleSelectSovItem = (rowId: number, sov: SovItemPayload) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              sovItemId: sov.id,
              aiaItemId: sov.aiaItemId,
              aiaItemCode: sov.aiaItemCode,
              aiaDescription: sov.description,
              subContractorId: sov.subContractor?.id ?? "",
              vendor: sov.subContractor?.companyName ?? "",
              amount: sov.amount?.amount ? String(sov.amount.amount) : "",
              sovAmount: sov.amount?.amount ?? null,
            }
          : item,
      ),
    );
  };

  const handleClearSovItem = (rowId: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              sovItemId: "",
              aiaItemId: "",
              aiaItemCode: null,
              aiaDescription: "",
              subContractorId: "",
              vendor: "",
              amount: "",
              sovAmount: null,
            }
          : item,
      ),
    );
  };

  const handleAttachInvoice = (rowId: number, key: string, name: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              invoiceAttached: true,
              invoiceKey: key,
              invoiceName: name,
            }
          : item,
      ),
    );
  };

  const handleRemoveInvoice = (rowId: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? { ...item, invoiceAttached: false, invoiceKey: "", invoiceName: "" }
          : item,
      ),
    );
  };

  const handleSelectVendor = (
    rowId: number,
    vendorId: string,
    vendorName: string,
  ) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? { ...item, subContractorId: vendorId, vendor: vendorName }
          : item,
      ),
    );
  };

  const handleAddVendor = (rowId: number) => {
    try {
      const stash: DrawReturnStash = {
        projectId: projectId ?? "",
        drawId: drawIdParam ?? "",
        rowId,
        drawDetails,
        lineItems,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(DRAW_RETURN_KEY, JSON.stringify(stash));
    } catch {}
    navigate("/sub-contractor", {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  const handleDeleteLine = (id: number) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleUpdateLine = (
    id: number,
    key: keyof DrawLineItem,
    value: string,
  ) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    );
  };

  const handleToggleOffsite = (id: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              offsiteStored: !item.offsiteStored,
              offsiteAmount: item.offsiteStored ? "" : item.offsiteAmount,
            }
          : item,
      ),
    );
  };

  const handleUpdateOffsiteAmount = (id: number, value: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, offsiteAmount: value } : item,
      ),
    );
  };

  const handleSubmit = async (gcSignatureKey: string) => {
    const payload: Record<string, unknown> = {
      drawNumber: drawDetails.drawNo,
      drawDate: drawDetails.date,
      toDate: drawDetails.periodTo,
      paymentInstructions: {
        payeeName: drawDetails.payee,
        mailToAddress: drawDetails.mailTo,
      },
      lienItems: lineItems.map((item) => {
        const lienItem: Record<string, unknown> = {
          subContractorId: item.subContractorId,
          aiaItemId: item.aiaItemId,
          totalAmount: {
            amount: parseCurrency(item.amount),
            currencyCode: "USD",
          },
        };
        if (item.notes) lienItem.description = item.notes;
        if (item.invoiceKey)
          lienItem.invoice = {
            key: item.invoiceKey,
            fileName: item.invoiceName || "Invoice",
          };
        if (item.offsiteStored && item.offsiteAmount)
          lienItem.storedItemAmount = {
            amount: parseCurrency(item.offsiteAmount),
            currencyCode: "USD",
          };
        return lienItem;
      }),
    };
    if (gcSignatureKey) payload.gcSignatureUrl = { key: gcSignatureKey };
    const { error } = (await saveGcDraw(projectId!, payload, true, drawId)) as {
      data: any;
      error: any;
    };
    if (error) {
      const msg = error.message || "Failed to submit draw";
      showToast(msg);
      if (msg.toLowerCase().includes("invoice")) {
        setLineItemsError(msg);
        goToStep(3);
      } else {
        setSubmitError(msg);
      }
      throw new Error(msg);
    }
    localStorage.removeItem(`draw_draft_${projectId}`);
    goToStep(6);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (lineItemsError) setLineItemsError("");
  }, [lineItems]);

  const handleNextFromLineItems = async () => {
    setSavingDraft(true);
    try {
      const payload = {
        drawNumber: drawDetails.drawNo,
        drawDate: drawDetails.date,
        toDate: drawDetails.periodTo,
        paymentInstructions: {
          payeeName: drawDetails.payee,
          mailToAddress: drawDetails.mailTo,
        },
        lienItems: lineItems.map((item) => {
          const lienItem: Record<string, unknown> = {
            subContractorId: item.subContractorId,
            aiaItemId: item.aiaItemId,
            totalAmount: {
              amount: parseCurrency(item.amount),
              currencyCode: "USD",
            },
          };
          if (item.notes) lienItem.description = item.notes;
          if (item.invoiceKey)
            lienItem.invoice = {
              key: item.invoiceKey,
              fileName: item.invoiceName || "Invoice",
            };
          if (item.offsiteStored && item.offsiteAmount)
            lienItem.storedItemAmount = {
              amount: parseCurrency(item.offsiteAmount),
              currencyCode: "USD",
            };
          return lienItem;
        }),
      };
      const { data, error } = (await saveGcDraw(
        projectId!,
        payload,
        false,
        drawId || "",
      )) as {
        data: any;
        error: any;
      };
      if (error) throw new Error(error.message || "Failed to save draw");
      const savedDrawId: string = data?.data?.id ?? data?.id ?? "";
      handleSetDrawId(savedDrawId);
    } catch (err: any) {
      const msg = err?.message || "Failed to save draw";
      setLineItemsError(msg);
    } finally {
      setSavingDraft(false);
    }
  };

  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <ConstructionProgressStep
          projectId={projectId || ""}
          onNext={() => goToStep(2)}
          drawNumber={drawDetails.drawNo}
          bankCompanyName={drawDetails?.bank ?? "the bank"}
        />
      );
    }
    if (currentStep === 2) {
      return (
        <DrawDetailsStep
          drawDetails={drawDetails}
          projectName={projectName}
          onChange={handleDrawDetailsChange}
          onNext={() => goToStep(3)}
          onBack={() => goToStep(1)}
          loading={projectLoading}
        />
      );
    }
    if (currentStep === 3) {
      return (
        <LineItemsStep
          projectId={projectId ?? ""}
          lineItems={lineItems}
          onAddLine={handleAddLine}
          onDeleteLine={handleDeleteLine}
          onUpdateLine={handleUpdateLine}
          onSelectSovItem={handleSelectSovItem}
          onClearSovItem={handleClearSovItem}
          onSelectVendor={handleSelectVendor}
          onAddVendor={handleAddVendor}
          onAttachInvoice={handleAttachInvoice}
          onRemoveInvoice={handleRemoveInvoice}
          onToggleOffsite={handleToggleOffsite}
          onUpdateOffsiteAmount={handleUpdateOffsiteAmount}
          onBack={() => goToStep(2)}
          onNext={handleNextFromLineItems}
          nextLoading={savingDraft}
          submitError={lineItemsError}
          totalAmount={totalAmount}
          loading={projectLoading}
          bankCompanyName={drawDetails?.bank ?? "the bank"}
        />
      );
    }
    if (currentStep === 4) {
      return (
        <LienWaiversStep
          projectId={projectId ?? ""}
          drawId={drawId}
          bankCompanyName={drawDetails.bank}
          onBack={() => goToStep(3)}
          onNext={() => goToStep(5)}
        />
      );
    }
    if (currentStep === 5) {
      return (
        <ReviewSubmitStep
          summary={summary}
          gcSigned={gcSigned}
          allInvoicesAttached={
            lineItems.length > 0 &&
            lineItems.every((item) => item.invoiceAttached)
          }
          onGcSignChange={setGcSigned}
          onBack={() => {
            setSubmitError("");
            goToStep(4);
          }}
          onSubmit={handleSubmit}
          submitError={submitError}
        />
      );
    }
    return (
      <SuccessStep
        onBackToProjects={() => navigate("/gc-dashboard")}
        drawNo={drawDetails.drawNo}
        totalAmount={totalAmount}
        bankName={drawDetails.bank}
      />
    );
  };

  return (
    <div className="drawRequestPage">
      <StepTabs
        steps={steps}
        currentStep={currentStep}
        successMode={currentStep === 6}
      />
      <main className="drawRequestContainer">{renderStep()}</main>
    </div>
  );
};

export default DrawRequestPage;
